import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, shareReplay, tap, finalize } from 'rxjs';
import { environment } from '@src/environments/environment';
import { DEVICE_TOKEN_KEY, SERVER_DEVICE_ID_KEY } from '@app/configs';
import { LoggerService } from '@app/shared/services/logger.service';

/**
 * Serwerowa tożsamość anonimowego urządzenia (deviceId + podpisany token).
 *
 * Backend wydaje parę przez POST /privacy/device-token. Klient zapisuje ją w
 * localStorage i wysyła `X-Device-Token` (+ zwrócone `X-Device-Id`) na endpointy
 * Privacy Center / consents wykonywane BEZ zalogowania. Dla zalogowanych użytkowników
 * tożsamość ustala JWT, więc te nagłówki są pomijane (patrz DeviceHeaderInterceptor).
 *
 * UWAGA: to NIE jest ten sam deviceId co w {@link DeviceService} — tamten jest
 * generowany lokalnie (UUID z prefixem aplikacji) i leci w body przy login/refresh/mfa.
 * Tutaj deviceId jest losowy, generowany i podpisany przez serwer.
 */
export interface DeviceIdentity {
  deviceId: string;
  deviceToken: string;
}

@Injectable({ providedIn: 'root' })
export class DeviceTokenService {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggerService);

  private _identity: DeviceIdentity | null = this._load();
  private _inFlight$: Observable<DeviceIdentity> | null = null;

  /**
   * Zwraca tożsamość urządzenia, w razie potrzeby wydając ją jednorazowo przez API.
   * Współbieżne wywołania współdzielą to samo żądanie (shareReplay + refCount).
   */
  ensureDeviceIdentity(): Observable<DeviceIdentity> {
    if (this._identity) {
      return of(this._identity);
    }

    if (!this._inFlight$) {
      this._inFlight$ = this.http
        .post<DeviceIdentity>(`${environment.apiUrl}privacy/device-token`, {})
        .pipe(
          tap((identity) => {
            this._identity = identity;
            this._save(identity);
          }),
          finalize(() => {
            // Pozwól ponowić wydanie tokenu, jeśli żądanie się nie powiodło.
            this._inFlight$ = null;
          }),
          shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    return this._inFlight$;
  }

  /** Synchroniczny odczyt zapamiętanej tożsamości (null jeśli brak). */
  getStoredIdentity(): DeviceIdentity | null {
    return this._identity;
  }

  private _load(): DeviceIdentity | null {
    try {
      const deviceId = localStorage.getItem(SERVER_DEVICE_ID_KEY);
      const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY);
      return deviceId && deviceToken ? { deviceId, deviceToken } : null;
    } catch (error) {
      this.logger.warn('[DeviceTokenService] Failed to read device identity from localStorage:', error);
      return null;
    }
  }

  private _save(identity: DeviceIdentity): void {
    try {
      localStorage.setItem(SERVER_DEVICE_ID_KEY, identity.deviceId);
      localStorage.setItem(DEVICE_TOKEN_KEY, identity.deviceToken);
    } catch (error) {
      this.logger.warn('[DeviceTokenService] Failed to persist device identity to localStorage:', error);
    }
  }
}
